import { queryClient } from '@/lib/queryClient';
import { api } from '@/lib/api';
import { BROWSER_TZ } from '@/lib/timezone';
import type { ActivityWeek, BinEntryType, BinList, HomeData, LinkRefs, PurgeResult } from '@shared/home';
import { addDays, dueDayKey, localDayKey, shortDay, zonedParts } from '@shared/home';
import type { FollowupItem, RestoreResult } from '@shared/types';

// Requests, query keys and small formatting helpers shared by Home, Activity this week and the Bin.

/** Day boundaries on Home and in the weekly view follow this browser's time zone. */
export const TZ = BROWSER_TZ;

export const homeKey = ['home', TZ] as const;
export const activityKey = (week: string) => ['activity', week, TZ] as const;
export const binKey = ['bin', TZ] as const;

export const fetchHome = (signal?: AbortSignal) => api.get<HomeData>('/home', { query: { tz: TZ }, signal });
export const fetchActivity = (week: string, signal?: AbortSignal) => api.get<ActivityWeek>('/activity', { query: { week, tz: TZ }, signal });
export const fetchBin = (signal?: AbortSignal) => api.get<BinList>('/bin', { query: { tz: TZ }, signal });
export const fetchLinks = (q: { personId?: string; companyId?: string; dealId?: string }) => api.get<LinkRefs>('/home/links', { query: q });

export function patchFollowup(id: string, body: { dueAt?: string; hasTime?: boolean; done?: boolean }) {
  return api.patch<FollowupItem>(`/followups/${id}`, body);
}

export function purgeItems(type: BinEntryType, ids: string[]) {
  return api.post<PurgeResult>('/bin/purge', { type, ids });
}

export function emptyBin() {
  return api.post<PurgeResult>('/bin/empty');
}

export function restoreItems(type: BinEntryType, ids: string[]) {
  if (type === 'tag') return Promise.all(ids.map((id) => api.post<{ tag: string; count: number }>(`/bin/tags/${id}/restore`)));
  return api.post<RestoreResult>('/bin/restore', { type, ids });
}

export function binItems(type: Exclude<BinEntryType, 'tag'>, ids: string[]) {
  return api.post<{ ids: string[] }>('/bin', { type, ids });
}

export function refreshHome() {
  return queryClient.invalidateQueries({ queryKey: ['home'] });
}

export function refreshBin() {
  return Promise.all([queryClient.invalidateQueries({ queryKey: ['bin'] }), queryClient.invalidateQueries({ queryKey: ['home'] })]);
}

const pad = (n: number) => String(n).padStart(2, '0');

/** "09:04" on the wall clock in `tz`. */
export function timeIn(at: string | Date, tz: string): string {
  const p = zonedParts(typeof at === 'string' ? new Date(at) : at, tz);
  return `${pad(p.hour)}:${pad(p.minute)}`;
}

const LONG_DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const LONG_MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

/** "Friday, 16 October 2026" for a day key. */
export function longDay(key: string): string {
  const [y, m, d] = key.split('-').map(Number) as [number, number, number];
  const weekday = (new Date(Date.UTC(y, m - 1, d)).getUTCDay() + 6) % 7;
  return `${LONG_DAYS[weekday]}, ${d} ${LONG_MONTHS[m - 1]} ${y}`;
}

/** "today", "tomorrow" or "Mon 19 Oct" for a day key, relative to `today`. */
export function relativeDay(key: string, today: string): string {
  if (key === today) return 'today';
  if (key === addDays(today, 1)) return 'tomorrow';
  if (key === addDays(today, -1)) return 'yesterday';
  return shortDay(key);
}

/** "today 16:00", "tomorrow", "Mon 19 Oct 09:00": when a follow-up is due, for toasts. */
export function duePhrase(f: { dueAt: string; hasTime: boolean }, now: Date, tz: string): string {
  const day = relativeDay(dueDayKey(f, tz), localDayKey(now, tz));
  return f.hasTime ? `${day} ${timeIn(f.dueAt, tz)}` : day;
}

/** The value PATCH and POST accept for a due date: the instant for timed items, the day for whole days. */
export function dueValue(f: { dueAt: string; hasTime: boolean }): string {
  return f.hasTime ? f.dueAt : f.dueAt.slice(0, 10);
}

/** "Maya Lindqvist" → "Maya", "Dr. Lena Hoffmann" → "Lena". */
export function firstName(name: string | null | undefined): string {
  const parts = (name ?? '').trim().split(/\s+/);
  const skip = parts.length > 1 && /^(dr|mr|mrs|ms|mx|miss|prof|sir|dame)\.?$/i.test(parts[0] ?? '') ? 1 : 0;
  return parts[skip] ?? '';
}

/** "Ines", "Ines and Felix", "Ines, Felix and Jonas". */
export function listNames(names: string[]): string {
  if (names.length <= 1) return names[0] ?? '';
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

/** "in 48 min", "in 2 h 10 min", "in 3 h". */
export function countdown(ms: number): string {
  const mins = Math.max(1, Math.ceil(ms / 60_000));
  if (mins < 60) return `in ${mins} min`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m ? `in ${h} h ${m} min` : `in ${h} h`;
}

/** "in 48 min" or "in 3 h 20 min" within the next 12 hours, then "tomorrow" or "in 3 days" by calendar day. */
export function startsIn(start: Date, now: Date, tz: string): string {
  const ms = start.getTime() - now.getTime();
  if (ms < 12 * 3_600_000) return countdown(ms);
  const dayNumber = (d: Date) => {
    const [y, m, day] = localDayKey(d, tz).split('-').map(Number) as [number, number, number];
    return Date.UTC(y, m - 1, day) / 86_400_000;
  };
  const days = dayNumber(start) - dayNumber(now);
  if (days <= 0) return countdown(ms);
  return days === 1 ? 'tomorrow' : `in ${days} days`;
}
